import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { UserProfile } from '../lib/firebase';
import { UserRole, UserProfileRecord } from '../types/user';
import {
  StoredKundaliRecord,
  getUserSavedKundalis,
  getAllKundalisAdmin,
  saveKundaliToCloud,
  deleteKundaliFromCloud,
  trackKundaliAccess
} from '../lib/kundaliService';
import { CompleteKundali, Language } from '../types/kundali';
import { calculateKundali } from '../utils/vedicEngine';
import { getZodiacName } from '../utils/language';
import { deduplicateStoredRecords } from '../utils/kundaliDedupe';
import { formatDate, formatDOB } from '../utils/dateUtils';
import { getUserAuthorizedProfiles } from '../lib/authorizationEngine';
import {
  FolderHeart,
  Save,
  Trash2,
  Eye,
  X,
  Search,
  Calendar,
  MapPin,
  Sparkles,
  CloudCheck,
  PlusCircle,
  RefreshCw,
  Globe,
  Lock,
  Users,
  Filter,
  Check
} from 'lucide-react';

interface MyKundalisDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserProfile | null;
  currentActiveKundali: CompleteKundali | null;
  onSelectKundali: (kundali: any) => void;
  lang?: Language;
  onOpenAuth: () => void;
  activeProfile?: UserProfileRecord | null;
  onDeleteKundali?: (id: string) => void;
}

export const MyKundalisDrawer: React.FC<MyKundalisDrawerProps> = ({
  isOpen,
  onClose,
  currentUser,
  currentActiveKundali,
  onSelectKundali,
  lang = 'en',
  onOpenAuth,
  activeProfile,
  onDeleteKundali
}) => {
  const isHi = lang === 'hi';
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN || (currentUser?.role as string) === 'super_admin';
  const [viewMode, setViewMode] = useState<'profile' | 'all'>(isSuperAdmin ? 'all' : 'profile');
  const [savedKundalis, setSavedKundalis] = useState<StoredKundaliRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingCurrent, setSavingCurrent] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProfileFilter, setSelectedProfileFilter] = useState<string>('all');
  const [searchAllProfilesScope, setSearchAllProfilesScope] = useState<boolean>(true);
  const [saveTargetProfile, setSaveTargetProfile] = useState<string>('self');
  const [notesInput, setNotesInput] = useState('');
  const [isPublicCheckbox, setIsPublicCheckbox] = useState(false);
  const [saveSuccessToast, setSaveSuccessToast] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load all authorized profiles (Self + Family Members)
  const userProfiles: UserProfileRecord[] = currentUser ? getUserAuthorizedProfiles(currentUser) : [];

  const loadUserKundalis = async () => {
    setLoading(true);
    try {
      if (!currentUser) {
        // Load guest cached charts from localStorage
        const cached = localStorage.getItem('astroveda_saved_kundalis_guest');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) {
            const records: StoredKundaliRecord[] = parsed.map((k: any) => ({
              id: k.id || `knd_local_${Date.now()}`,
              userId: 'guest',
              userEmail: 'guest@astroveda.local',
              createdByName: 'Guest User',
              name: k.birthDetails?.name || 'Saved Chart',
              gender: k.birthDetails?.gender || 'male',
              date: k.birthDetails?.date || '',
              time: k.birthDetails?.time || '',
              placeName: k.birthDetails?.placeName || '',
              latitude: k.birthDetails?.latitude || 0,
              longitude: k.birthDetails?.longitude || 0,
              timezone: k.birthDetails?.timezoneOffsetMinutes || 330,
              chartStyle: 'north',
              tags: ['Local Cache'],
              notes: k.notes || '',
              isPublic: false,
              createdAt: k.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              lastAccessedAt: new Date().toISOString(),
              kundaliData: k
            }));
            setSavedKundalis(records);
          }
        }
        return;
      }

      let records: StoredKundaliRecord[] = [];
      if (viewMode === 'all' && isSuperAdmin) {
        records = await getAllKundalisAdmin();
      } else {
        records = await getUserSavedKundalis(currentUser.uid);
      }
      
      // Always deduplicate saved records to avoid duplicate names/charts in all views
      const uniqueRecords = deduplicateStoredRecords(records);
      setSavedKundalis(uniqueRecords);
    } catch (err) {
      console.error('Failed loading saved kundalis:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadUserKundalis();
    }
  }, [currentUser, viewMode, isOpen]);

  if (!isOpen) return null;

  // Determine associated profile for a Kundali
  const getAssociatedProfile = (k: StoredKundaliRecord): UserProfileRecord | undefined => {
    return userProfiles.find((p) => {
      if (k.tags?.some((t) => t.toLowerCase() === p.name.toLowerCase() || t === p.profileId || t.toLowerCase() === p.relationship.toLowerCase())) {
        return true;
      }
      if (k.name.trim().toLowerCase() === p.name.trim().toLowerCase()) {
        return true;
      }
      return false;
    });
  };

  // Save current active Kundali to Firestore
  const handleSaveCurrentKundali = async () => {
    if (!currentUser) {
      onOpenAuth();
      return;
    }
    if (!currentActiveKundali) return;

    setSavingCurrent(true);
    try {
      const chosenProf = userProfiles.find((p) => p.profileId === saveTargetProfile || p.relationship === saveTargetProfile);
      const profileTags = chosenProf 
        ? [chosenProf.name, chosenProf.relationship, 'Personal']
        : ['Personal'];

      const record = await saveKundaliToCloud(
        currentActiveKundali,
        currentUser,
        currentActiveKundali.id || currentActiveKundali.cloudId || undefined,
        profileTags,
        notesInput,
        isPublicCheckbox
      );

      setSavedKundalis((prev) => {
        const idx = prev.findIndex((k) => k.id === record.id);
        if (idx !== -1) {
          const next = [...prev];
          next[idx] = record;
          return next;
        }
        return deduplicateStoredRecords([record, ...prev]);
      });
      setNotesInput('');
      setSaveSuccessToast(isHi ? 'कुण्डली वॉल्ट में सुरक्षित सहेजी गई!' : 'Kundali saved to your vault!');
      setTimeout(() => setSaveSuccessToast(null), 3000);
    } catch (err: any) {
      setSaveSuccessToast(isHi ? 'सहेजने में त्रुटि: ' + (err.message || 'त्रुटि') : 'Failed to save Kundali: ' + (err.message || 'Unknown error'));
      setTimeout(() => setSaveSuccessToast(null), 4000);
    } finally {
      setSavingCurrent(false);
    }
  };

  // Delete Kundali
  const handleDeleteClick = (id: string, name: string) => {
    setItemToDelete({ id, name });
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    const { id, name } = itemToDelete;
    setIsDeleting(true);
    try {
      // 1. Delete from cloud Firestore if doc exists
      try {
        await deleteKundaliFromCloud(id);
      } catch (cloudErr) {
        console.warn('Cloud delete notice:', cloudErr);
      }

      // 2. Remove from local drawer state
      setSavedKundalis((prev) => prev.filter((k) => k.id !== id));

      // 3. Remove from user or guest localStorage cache
      const cacheKey = currentUser?.uid 
        ? `astroveda_saved_kundalis_${currentUser.uid}` 
        : 'astroveda_saved_kundalis_guest';
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) {
            const filtered = parsed.filter((k: any) => k.id !== id && (k as any).cloudId !== id);
            localStorage.setItem(cacheKey, JSON.stringify(filtered));
          }
        }
      } catch (storageErr) {
        console.warn('localStorage deletion notice:', storageErr);
      }

      // 4. Notify parent app if callback provided
      if (onDeleteKundali) {
        onDeleteKundali(id);
      }

      setSaveSuccessToast(isHi ? `"${name}" कुण्डली हटा दी गई` : `"${name}" removed from vault`);
      setTimeout(() => setSaveSuccessToast(null), 3000);
    } catch (err: any) {
      console.error('Delete error:', err);
      setSaveSuccessToast(isHi ? 'हटाने में त्रुटि हुई' : 'Failed to delete Kundali');
      setTimeout(() => setSaveSuccessToast(null), 3000);
    } finally {
      setIsDeleting(false);
      setItemToDelete(null);
    }
  };

  // Filter Kundalis by selected profile and search term
  const filteredKundalis = savedKundalis.filter((k) => {
    // If a specific profile is selected and "searchAllProfilesScope" is not explicitly forced
    if (selectedProfileFilter !== 'all' && !searchAllProfilesScope) {
      const assoc = getAssociatedProfile(k);
      const targetProf = userProfiles.find((p) => p.profileId === selectedProfileFilter);
      if (targetProf) {
        const matchesName = k.name.trim().toLowerCase() === targetProf.name.trim().toLowerCase();
        const matchesTag = k.tags?.some((t) => t.toLowerCase() === targetProf.name.toLowerCase() || t === targetProf.profileId || t.toLowerCase() === targetProf.relationship.toLowerCase());
        if (!matchesName && !matchesTag) {
          return false;
        }
      }
    }

    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    const assoc = getAssociatedProfile(k);
    return (
      k.name.toLowerCase().includes(q) ||
      k.placeName.toLowerCase().includes(q) ||
      k.date.includes(q) ||
      (k.notes && k.notes.toLowerCase().includes(q)) ||
      (k.tags && k.tags.some((t) => t.toLowerCase().includes(q))) ||
      (assoc && (assoc.name.toLowerCase().includes(q) || assoc.relationship.toLowerCase().includes(q))) ||
      (isSuperAdmin && k.userEmail && k.userEmail.toLowerCase().includes(q))
    );
  });

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-end bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-astro-bg border-l border-astro-border w-full max-w-md h-full p-6 flex flex-col shadow-2xl relative overflow-y-auto animate-slideInRight text-astro-text"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-astro-border">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-astro-primary/10 border border-astro-primary/20 flex items-center justify-center text-astro-primary">
              <FolderHeart className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-astro-text font-serif">
                {lang === 'hi' ? 'ऑल प्रोफाइल कुण्डली वॉल्ट' : 'All Profile Kundali Vault'}
              </h2>
              <p className="text-[11px] text-astro-secondary-text">
                {currentUser ? currentUser.email : 'Login to save & sync charts'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-astro-secondary-text hover:text-astro-text rounded-xl bg-astro-card border border-astro-border"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Not Logged In Prompt */}
        {!currentUser ? (
          <div className="my-auto text-center py-12 px-4 space-y-4">
            <div className="w-16 h-16 rounded-3xl bg-astro-primary/10 border border-astro-primary/20 flex items-center justify-center text-astro-primary mx-auto">
              <Sparkles className="w-8 h-8 text-astro-accent" />
            </div>
            <h3 className="text-lg font-bold text-astro-text font-serif">
              {lang === 'hi' ? 'लॉगिन आवश्यक है' : 'Account Identity Required'}
            </h3>
            <p className="text-xs text-astro-secondary-text">
              {lang === 'hi'
                ? 'अपनी कुंडलियाँ सहेजने और किसी भी डिवाइस से देखने के लिए लॉगिन करें।'
                : 'Sign in to access your cloud-saved birth charts across any session.'}
            </p>
            <button
              onClick={() => {
                onClose();
                onOpenAuth();
              }}
              className="w-full py-3 bg-astro-primary text-white font-bold text-xs rounded-xl hover:bg-[#123d3c] transition-all shadow-md shadow-astro-primary/15"
            >
              {lang === 'hi' ? 'अभी लॉगिन करें' : 'Sign In / Register Profile'}
            </button>
          </div>
        ) : (
          <div className="flex-1 flex flex-col pt-4 space-y-4">
            {/* View Mode Switcher for Admins */}
            {isSuperAdmin && (
              <div className="flex bg-astro-card border border-astro-border p-1 rounded-xl">
                <button
                  onClick={() => setViewMode('all')}
                  className={`flex-1 text-center py-2 text-[11px] font-bold rounded-lg transition-all ${
                    viewMode === 'all'
                      ? 'bg-astro-primary text-white shadow-sm'
                      : 'text-astro-secondary-text hover:text-astro-primary'
                  }`}
                >
                  {lang === 'hi' ? 'समस्त सिस्टम कुंडलियाँ (एडमिन/सुपर)' : 'All Profile Kundali Vault'}
                </button>
                <button
                  onClick={() => setViewMode('profile')}
                  className={`flex-1 text-center py-2 text-[11px] font-bold rounded-lg transition-all ${
                    viewMode === 'profile'
                      ? 'bg-astro-primary text-white shadow-sm'
                      : 'text-astro-secondary-text hover:text-astro-primary'
                  }`}
                >
                  {lang === 'hi' ? 'मेरी प्रोफाइल कुंडलियाँ' : 'My Saved Profiles'}
                </button>
              </div>
            )}

            {/* Quick Save Current Active Kundali Card */}
            {currentActiveKundali && (
              <div className="p-4 bg-astro-card rounded-2xl border border-astro-primary/30 space-y-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Save className="w-4 h-4 text-astro-primary" />
                    <span className="text-xs font-bold text-astro-text">
                      {isHi ? 'वर्तमान कुंडली सहेजें' : 'Save Active Chart to Vault'}
                    </span>
                  </div>
                  <span className="text-[10px] text-astro-primary font-bold uppercase">
                    {currentActiveKundali.birthDetails.name}
                  </span>
                </div>

                {/* Profile selection for saved Kundali */}
                {userProfiles.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-astro-secondary-text shrink-0">
                      {isHi ? 'प्रोफाइल:' : 'Profile:'}
                    </span>
                    <select
                      value={saveTargetProfile}
                      onChange={(e) => setSaveTargetProfile(e.target.value)}
                      className="flex-1 px-2.5 py-1.5 bg-astro-bg border border-astro-border rounded-xl text-xs text-astro-text focus:outline-none focus:border-astro-primary font-medium"
                    >
                      {userProfiles.map((p) => (
                        <option key={p.profileId} value={p.profileId}>
                          {p.name} ({p.relationship === 'self' ? (isHi ? 'स्वयं' : 'Self') : p.relationship})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <input
                  type="text"
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  placeholder={isHi ? 'टिप्पणी (ऐच्छिक)...' : 'Add private notes (optional)...'}
                  className="w-full px-3 py-2 bg-astro-bg border border-astro-border rounded-xl text-xs text-astro-text focus:outline-none focus:border-astro-primary"
                />

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-astro-secondary-text cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isPublicCheckbox}
                      onChange={(e) => setIsPublicCheckbox(e.target.checked)}
                      className="rounded border-astro-border bg-astro-bg text-astro-primary focus:ring-0"
                    />
                    <span>{isHi ? 'सार्वजनिक रूप से साझा करें' : 'Make Publicly Shared'}</span>
                  </label>

                  <button
                    onClick={handleSaveCurrentKundali}
                    disabled={savingCurrent}
                    className="px-4 py-2 bg-astro-primary text-white font-bold text-xs rounded-xl hover:bg-[#123d3c] transition-all flex items-center gap-1.5 disabled:opacity-50 shadow-sm cursor-pointer"
                  >
                    {savingCurrent ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <CloudCheck className="w-3.5 h-3.5 text-astro-accent" />
                    )}
                    {isHi ? 'सहेजें' : 'Save Chart'}
                  </button>
                </div>

                {saveSuccessToast && (
                  <p className="text-[11px] text-astro-primary font-semibold text-center pt-1 animate-fadeIn">
                    ✓ {saveSuccessToast}
                  </p>
                )}
              </div>
            )}

            {/* Profile Filter Bar & Scope Options */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1">
                  <Users className="w-3.5 h-3.5 text-astro-primary" />
                  {isHi ? 'प्रोफाइल अनुसार खोजें / फिल्टर' : 'Search & Filter by Profile'}
                </span>
                
                {/* Search across all profiles toggle */}
                <label className="inline-flex items-center gap-1.5 text-[11px] font-medium text-astro-text cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={searchAllProfilesScope}
                    onChange={(e) => setSearchAllProfilesScope(e.target.checked)}
                    className="rounded border-astro-border text-astro-primary focus:ring-0"
                  />
                  <span>{isHi ? 'सभी प्रोफाइलों में खोजें' : 'All Profiles'}</span>
                </label>
              </div>

              {/* Profile Chips */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 custom-horizontal-scrollbar">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProfileFilter('all');
                  }}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    selectedProfileFilter === 'all'
                      ? 'bg-astro-primary text-white shadow-xs'
                      : 'bg-astro-card border border-astro-border text-astro-secondary-text hover:text-astro-text'
                  }`}
                >
                  {isHi ? 'समस्त प्रोफाइल' : 'All Profiles'} ({savedKundalis.length})
                </button>

                {userProfiles.map((p) => {
                  const isSelected = selectedProfileFilter === p.profileId;
                  const profileKundaliCount = savedKundalis.filter((k) => {
                    const assoc = getAssociatedProfile(k);
                    return (
                      assoc?.profileId === p.profileId ||
                      k.name.trim().toLowerCase() === p.name.trim().toLowerCase() ||
                      k.tags?.some((t) => t.toLowerCase() === p.name.toLowerCase() || t === p.profileId)
                    );
                  }).length;

                  return (
                    <button
                      key={p.profileId}
                      type="button"
                      onClick={() => {
                        setSelectedProfileFilter(p.profileId);
                        setSearchAllProfilesScope(false);
                      }}
                      className={`px-2.5 py-1 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1 cursor-pointer ${
                        isSelected
                          ? 'bg-astro-primary text-white shadow-xs'
                          : 'bg-astro-card border border-astro-border text-astro-secondary-text hover:text-astro-text'
                      }`}
                    >
                      <span>{p.name}</span>
                      <span className="text-[10px] opacity-80">({profileKundaliCount})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search Input */}
            <div className="space-y-1">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-astro-secondary-text" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={
                    searchAllProfilesScope
                      ? (isHi ? 'सभी प्रोफाइलों में कुंडलियाँ खोजें (नाम, स्थान, जन्मतिथि)...' : 'Search Kundalis across all profiles (name, city, date)...')
                      : (isHi ? 'चयनित प्रोफाइल में कुंडलियाँ खोजें...' : 'Search within selected profile...')
                  }
                  className="w-full pl-9 pr-8 py-2 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text placeholder:text-astro-muted focus:outline-none focus:border-astro-primary"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-astro-secondary-text hover:text-astro-text"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="flex items-center justify-between text-[10px] text-astro-secondary-text px-1">
                <span>
                  {isHi ? 'परिणाम:' : 'Showing:'} {filteredKundalis.length} {isHi ? 'कुंडलियाँ' : 'Kundalis'}
                  {searchAllProfilesScope ? (isHi ? ' (समस्त प्रोफाइल)' : ' (Across all profiles)') : ''}
                </span>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="text-astro-primary font-bold hover:underline"
                  >
                    {isHi ? 'खोज हटाएं' : 'Clear search'}
                  </button>
                )}
              </div>
            </div>

            {/* Kundalis List */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {loading ? (
                <div className="py-12 text-center text-astro-secondary-text">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-astro-primary mb-2" />
                  <p className="text-xs">{isHi ? 'क्लाउड वॉल्ट से कुंडलियाँ लोड हो रही हैं...' : 'Fetching saved charts from Cloud Vault...'}</p>
                </div>
              ) : filteredKundalis.length === 0 ? (
                <div className="py-12 text-center text-astro-secondary-text bg-astro-card rounded-2xl border border-astro-border space-y-1.5">
                  <FolderHeart className="w-8 h-8 mx-auto text-astro-secondary-text mb-1" />
                  <p className="text-xs font-semibold text-astro-text">
                    {searchTerm 
                      ? (isHi ? 'कोई मेल खाती कुंडली नहीं मिली' : 'No matching charts found')
                      : (isHi ? 'इस प्रोफाइल में कोई कुंडली नहीं है' : 'No Saved Charts Yet')}
                  </p>
                  <p className="text-[11px] text-astro-secondary-text">
                    {searchTerm 
                      ? (isHi ? 'अन्य नाम, स्थान या "समस्त प्रोफाइल" विकल्प का प्रयास करें।' : 'Try another name, city, or toggle "All Profiles".')
                      : (isHi ? 'नई कुंडली बनाएं और ऊपर "सहेजें" पर क्लिक करें!' : 'Generate a chart and click "Save Chart" above!')}
                  </p>
                </div>
              ) : (
                filteredKundalis.map((item) => {
                  const assoc = getAssociatedProfile(item);
                  return (
                    <div
                      key={item.id}
                      onClick={() => {
                        onSelectKundali(item);
                        onClose();
                      }}
                      className="p-3.5 bg-astro-card hover:bg-astro-bg border border-astro-border hover:border-astro-primary/50 rounded-2xl transition-all space-y-2 group shadow-xs cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-sm text-astro-primary group-hover:underline">{item.name}</h4>
                            {/* Profile association badge */}
                            {assoc ? (
                              <span className="px-1.5 py-0.2 rounded-md text-[9px] font-bold bg-[#E0F2FE] text-[#0369A1] border border-[#BAE6FD]">
                                👤 {assoc.name} ({assoc.relationship === 'self' ? (isHi ? 'स्वयं' : 'Self') : assoc.relationship})
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.2 rounded-md text-[9px] font-medium bg-[#F3F4F6] text-[#4B5563] border border-[#E5E7EB]">
                                {isHi ? 'वॉल्ट' : 'Vault'}
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-astro-secondary-text font-mono block mt-0.5">
                            {formatDOB(item.date)} • {item.time}
                          </span>
                          <span className="text-[10px] text-astro-secondary-text/70 font-mono block">
                            {isHi ? 'सहेजा गया:' : 'Saved:'} {formatDate(item.createdAt)}
                          </span>
                          {isSuperAdmin && item.userEmail && (
                            <span className="text-[10px] text-[#6F9690] font-mono block">
                              By: {item.userEmail}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(item.id, item.name);
                            }}
                            className="p-2 bg-astro-bg hover:bg-red-500/10 text-astro-secondary-text hover:text-red-600 border border-astro-border rounded-lg transition-all shadow-xs"
                            title="Delete Chart"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-[11px] text-astro-secondary-text pt-1 border-t border-astro-border/60">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-astro-primary" />
                          {item.placeName}
                        </span>
                        <span className="text-[10px] font-mono">
                          {item.isPublic ? (
                            <span className="text-astro-primary flex items-center gap-0.5 font-semibold">
                              <Globe className="w-2.5 h-2.5" /> {isHi ? 'सार्वजनिक' : 'Public'}
                            </span>
                          ) : (
                            <span className="text-astro-secondary-text flex items-center gap-0.5">
                              <Lock className="w-2.5 h-2.5" /> {isHi ? 'निजी' : 'Private'}
                            </span>
                          )}
                        </span>
                      </div>

                      {item.notes && (
                        <p className="text-[11px] text-astro-text bg-astro-bg p-2 rounded-lg italic border border-astro-border">
                          "{item.notes}"
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* In-App Confirmation Modal for Chart Deletion */}
      {itemToDelete && (
        <div
          className="fixed inset-0 z-70 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs"
          onClick={(e) => {
            e.stopPropagation();
            if (!isDeleting) setItemToDelete(null);
          }}
        >
          <div
            className="bg-astro-card border border-astro-border rounded-2xl p-5 max-w-sm w-full shadow-2xl space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-full bg-red-100 text-red-600 shrink-0">
                <Trash2 className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-sm text-astro-primary-text">
                  {isHi ? 'कुण्डली हटाएं?' : 'Delete Kundali?'}
                </h3>
                <p className="text-xs text-astro-secondary-text mt-1">
                  {isHi 
                    ? `क्या आप वास्तव में "${itemToDelete.name}" को अपने सहेजे गए वॉल्ट से हटाना चाहते हैं?` 
                    : `Are you sure you want to remove "${itemToDelete.name}" from your saved vault?`}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-astro-border/60">
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => setItemToDelete(null)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-astro-border hover:bg-astro-bg text-astro-secondary-text transition-colors"
              >
                {isHi ? 'रद्द करें' : 'Cancel'}
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={handleConfirmDelete}
                className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center gap-1.5 shadow-xs disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>{isHi ? 'हटा रहे हैं...' : 'Deleting...'}</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>{isHi ? 'हटाएं' : 'Delete'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};
